/**
 * @author        woody@zumper.com (Woody Anderson)
 * @copyright     Copyright (c) 2017, Zumper
 * @description   API Actions with effects
 */

import {
  AutocompleteResults,
  BuildingReviewsData,
  CityGuideCity,
  CityLifestyles,
  ContentModuleAmenities,
  DetailLink,
  EngrainMap,
  GlobalNavLinks,
  HoodLifestyles,
  LifestyleScore,
  MetroRentReport,
  MlsListing,
  RentHistoryDetail,
  RentReport,
  RentalData,
  RentalDataHomepage,
  RentalDataLocation,
  RentalsUnitedAvailability,
  ResidentQuotes,
  ShortTermCancellationPolicies,
  ShortTermPolicies,
  SitemapCountsResponse,
  SitemapResponse,
  StreamConversationResponse,
  StreamTokenResponse,
  TourAvailability,
  TourIntegration,
  Unit,
  UnreadCounts,
  User,
} from '@zcode/bizos'
import { localStorageSingleton } from '@zcode/nr-utils'
import { Listing as ListingType, NormalListingData } from '@zcode/state-type'
import AdditionalBuildingData from '../bizos/additional_building_data'
import Blog from '../bizos/blog'
import { BuildingOrAddress } from '../bizos/building_or_address'
import City, { MCity } from '../bizos/city'
import { CityGuideNeighborhoodData } from '../bizos/cityguide_neighborhood'
import CountsBy from '../bizos/counts_by'
import { DlcMessagedProperties } from '../bizos/dlc_messaged'
import FaqData from '../bizos/faq_data'
import Geos from '../bizos/geos'
import Listable from '../bizos/listable'
import Listables from '../bizos/listables'
import Neighborhood, { MNeighborhood } from '../bizos/neighborhood'
import { NewPriceData } from '../bizos/new_price_data'
import { PermanentAddress } from '../bizos/permanent_address'
import Pin from '../bizos/pin'
import Pins from '../bizos/pins'
import Poi from '../bizos/poi'
import { Pois } from '../bizos/pois'
import { ProConData } from '../bizos/pro_con_data'
import { Building, Listing } from '../bizos/property'
import Ratings from '../bizos/ratings'
import RecommendedListablesForYou from '../bizos/recommended_for_you'
import Reviews from '../bizos/reviews'
import { DistrictDetails, LocationSchool } from '../bizos/schools'
import { SavedSearch } from '../bizos/searches'
import TourRequest from '../bizos/tour_request'
import { vrboPriceAndAvailability as vrboPriceAndAvailabilityBizo } from '../bizos/vrbo_price_and_availability'
import Walkscore from '../bizos/walkscore'
import Bundle from './bundle'
import apiFetcher from './fetcher.singleton'
import zrsrc from './zrsrc'

export const bundle = Bundle

let defaultInitializer = bundle

if (typeof window !== 'undefined') {
  const state = window.__PRELOADED_STATE__ ?? {}
  if (state?.app?.bot) {
    defaultInitializer = Promise.resolve()
  }
}

const fetcher = (url, Bizo, actions = {}, config = {}, initializer = defaultInitializer) =>
  zrsrc(apiFetcher, url, actions, config, Bizo, initializer)

/**
 * this function injects the P,O,V function into a data body prior to request sent to server. These
 * promo tokens will be ignored if `encodedFilters` is set. `encodedFilters` filter is used for the
 * internal chrome extension tool Flaura
 *
 * @param params
 * @param data
 */
const injectPromo = (params, data) => {
  if (data.P === undefined && !data.encodedFilters) {
    for (const p of ['P', 'O', 'V']) {
      const pv = localStorageSingleton.getx(`_${p}`)
      if (pv !== undefined) {
        data[p] = pv
      }
    }
  }
}

// export const bundle
export const redirect = fetcher('/api/t/1/pages/redirect/:listingId', Listing)
/**
 * Listings API
 *
 * @type {{
 * get(params: { listingId: number | string }, options?: RequestInit): Promise<ListingType<NormalListingData>>,
 * }}
 */
export const listings = fetcher('/api/t/1/pages/listings/:listingId', Listing)
/**
 * Buildings API
 *
 * @type {{
 * get(params: { buildingId: number | string; includeWithdrawnFloorplans?: boolean; includeSupplemental?: boolean }, options?: RequestInit): Promise<InstanceType<Building>>,
 * }}
 */
export const buildings = fetcher('/api/t/1/pages/buildings/:buildingId', Building)
/**
 * Address API
 *
 * @type {{query: (function({ pa_url: `${string}-${'can' | 'usa'}` }): Promise<import('@zcode/state-type').PermanentAddressApiResponse>)}}
 */
export const address = fetcher('/api/t/1/pages/address', PermanentAddress, {
  query: { method: 'POST' },
})
/**
 * Address API
 *
 * @type {{get(params: {
 *   limit?: number,
 *   offset?: number,
 *   paUrl: import('@zcode/bizos').PAUrl,
 *   prodPaUrl?: import('@zcode/bizos').PAUrl,
 *   since?: string, // YYYY-MM-DD: Inclusive date for when to start getting data from
 * }): Promise<RentHistoryDetail>}}
 */
export const addressRentHistory = fetcher(
  '/api/t/1/addresses/:paUrl/rent_history',
  RentHistoryDetail
)

/**
 * Listables API
 *
 * @type {{
 *   clearCache: () => void,
 *   query: (function(*): Promise<import('@zcode/state-type').Listables>)
 * }}
 */
export const listables = fetcher('/api/t/1/pages/listables', Listables, {
  query: { method: 'POST' },
  _pre: injectPromo,
})

/**
 * mapListSearch API
 *
 * @type {{
 *   clearCache: () => void,
 *   query: (function(*): Promise<import('@zcode/state-type').Listables>)
 * }}
 */
export const inventorySearch = fetcher('/api/svc/inventory/v1/listables/search', Listables, {
  query: { method: 'POST' },
  _pre: injectPromo,
})

/**
 * mapListSearch API
 *
 * @type {{
 *   clearCache: () => void,
 *   query: (function(*): Promise<import('@zcode/state-type').Listables>)
 * }}
 */
export const mapListSearch = fetcher('/api/svc/inventory/v1/listables/maplist/search', Listables, {
  query: { method: 'POST' },
  _pre: injectPromo,
})

/**
 * mapListPins API
 *
 * @type {{
 *   clearCache: () => void,
 *   query: (function(*): Promise<import('@zcode/state-type').PinsBizo>)
 * }}
 */
export const mapListPins = fetcher('/api/svc/inventory/v1/listables/maplist/pins', Pins, {
  query: { method: 'POST' },
  _pre: injectPromo,
})

/**
 * mapListPins API
 *
 * @type {{query: (function(*): Promise<{[key: string]: {[key: string]: number }}>)}}
 */
export const listablesCount = fetcher('/api/svc/inventory/v1/listables/counts', undefined, {
  query: { method: 'POST' },
  _pre: injectPromo,
})

/**
 * Featured Listables API
 *
 * @type {{query: (function(*): Promise<{listables: import('@zcode/state-type').RawListable[], matching: number}>)}}
 */
export const mapListFeatured = fetcher(
  '/api/svc/inventory/v1/listables/maplist/featured',
  undefined,
  {
    query: { method: 'POST' },
  }
)
/**
 * Spotlight Listables API
 *
 * @type {{query: (function(*): Promise<{listables: import('@zcode/state-type').RawListable[], matching: number}>)}}
 */
export const maplistSpotlight = fetcher(
  '/api/svc/inventory/v1/listables/maplist/spotlight',
  undefined,
  {
    query: { method: 'POST' },
  }
)
/**
 * Zoe Chat Start API
 *
 * @type {{query: (function(import('@zcode/state-type').AIAssistantStartPayload): Promise<import('@zcode/state-type').AIAssistantStartResponse>)}}
 * */
export const aiAssistantChatStart = fetcher('/api/svc/chatbot/1/start', undefined, {
  query: { method: 'POST' },
})
/**
 * Zoe Map/List Search API
 *
 * @type {{query: (function({q: string, lat: number, lng: number}): Promise<import('@zcode/state-type').AIAssistantSearchResponse>)}}
 */
export const aiAssistantSearchFilters = fetcher('/api/svc/chatbot/1/filters', undefined, {
  query: { method: 'GET' },
})

/**
 * Additional Building Data API
 *
 * @type {{get: (function(*): Promise<any>)}}
 */
export const additionalBuildingData = fetcher(
  '/api/t/1/pages/listables/additional_building_data/:buildingId',
  AdditionalBuildingData,
  { get: { method: 'GET' } }
)
/**
 * Featured Listables API
 *
 * @type {{query: (function(*): Promise<import('@zcode/state-type').RawListable[]>)}}
 */
export const featured = fetcher('/api/t/1/listables/featured', undefined, {
  query: { method: 'POST' },
})

/**
 * City API
 *
 * @type {{get: (function(*): Promise<import('@zcode/state-type').City>)}}
 */
export const cities = fetcher('/api/t/1/pages/cities/:url', City)

/**
 * Neighborhood API
 *
 * @type {{get: (function(*): Promise<import('@zcode/state-type').Neighborhood>)}}
 */
export const neighborhoods = fetcher('/api/t/1/pages/neighborhoods/:url', Neighborhood)
/**
 * @type {{post: (query: {minLat?: number | null, maxLat?: number | null, minLng?: number | null, maxLng?: number | null, limit?: number, urls?: string[]}) => Promise<import('@zcode/state-type').MCity[]}}
 */
export const mcities = fetcher('/api/t/1/minimal_cities', MCity, { query: { method: 'POST' } })
/**
 * @type {{get: (query: {hoodType?: number, minLat?: number | null, maxLat?: number | null, minLng?: number | null, maxLng?: number | null, urls?: string[]}) => Promise<import('@zcode/state-type').MNeighborhood[]}}
 */
export const mneighborhoods = fetcher('/api/t/1/hoods/', MNeighborhood)

/**
 * @type {{query: (function(*): Promise<ReadonlyArray<import('@zcode/state-type').Pin>>>)}}
 */
export const pins = fetcher('/api/t/1/pins', Pin, { query: { method: 'POST' }, _pre: injectPromo })

/**
 * Geos API
 *
 * @type {{get: (function(*): Promise<import('@zcode/state-type').Geo>)}}
 */
export const geos = fetcher('/api/t/1/geos', Geos)

// TODO: This type isn't quite right: `count.query({ all: true , queries: [] })`
// should return `{ all: number }`, but currently is typed as `{ all: number, queries?: readonly number[] }`
/**
 * @type {{query: <
 *   Query extends { all?: boolean, per?: boolean, queries: readonly object[] }
 * >(query: Query) => Promise<
 *   (Query['all'] extends true
 *     ? { all: number }
 *     : Query['all'] extends false | undefined
 *       ? {}
 *       : { all?: number })
 *   & (Query['per'] extends true
 *     ? { queries: readonly number[] }
 *     : Query['per'] extends false | undefined
 *       ? {}
 *       : { queries?: readonly number[] })
 * >}}
 */
export const count = fetcher('/api/t/1/listables/count', undefined, { query: { method: 'POST' } })

/**
 * @type {{query: (function(*): Promise<any>)}}
 */
export const countsBy = fetcher('/api/t/1/listables/counts_by', CountsBy, {
  query: { method: 'POST' },
})

/**
 * PriceData API
 *
 * @type {{get: (function({ region: string }): Promise<import('@zcode/state-type').NewPriceData>)}}
 */
export const newPriceData = fetcher('/api/x/1/columnar_price_data', NewPriceData)
/**
 * @type {{
 *   clear(...args: any[]): Promise<any>
 *   save(search: any): Promise<import('@zcode/state-type').SavedSearch & { _data: { undo: { 'py/b64': string }; search_id: number; status: 201 } }>
 *   update(...args: any[]): Promise<any>
 *   remove(params: { searchId: number }): Promise<void>
 * }}
 */
export const search = fetcher('/api/t/1/searches/:searchId', SavedSearch, {
  clear: { method: 'DELETE' },
  save: { method: 'POST' },
  update: { method: 'PUT' },
  remove: { method: 'DELETE' },
})

/**
 * @type {{ get(params: { sitemap_host?: import('@zcode/enums').SitemapHost }, options?: RequestInit): Promise<SitemapCountsResponse> }}
 */
export const sitemapCounts = fetcher('/api/t/1/sitemap/count', SitemapCountsResponse)
/**
 * @type {{ get(params: { limit: number; offset: number; type: string; sitemap_host?: import('@zcode/enums').SitemapHost }, options?: RequestInit): Promise<SitemapResponse> }}
 */
export const sitemapUrls = fetcher('/api/t/1/sitemap/:type', SitemapResponse)
/**
 * @type {{ get: (function({ hood: string } | { city: string }): Promise<any> )}}
 */
export const sitemapVerticals = fetcher('/api/t/1/sitemap_verticals', undefined, {}, {}, null)

export const poi = fetcher('/api/t/1/poi', Poi, { get: { method: 'GET', serverRetry: 0 } })
export const walkscore = fetcher('/api/x/1/walkscore', Walkscore)
export const blog = fetcher('/api/t/1/blog', Blog)

/**
 * @type {{ get(params: { addressId: number }): Promise<import('@zcode/state-type').ProConData> }}
 */
export const proConV2AddressData = fetcher('/api/x/1/pros_cons/addresses/:addressId', ProConData)

/**
 * @type {{ get(params: { buildingId: number }): Promise<import('@zcode/state-type').ProConData> }}
 */
export const proConV2BuildingData = fetcher('/api/x/1/pros_cons/buildings/:buildingId', ProConData)

/**
 * @type {{ get(params: { buildingId: number }): Promise<BuildingReviewsData> }}
 */
export const buildingReviewsData = fetcher(
  '/api/x/1/ratings_reviews/buildings/:buildingId',
  BuildingReviewsData
)

// non-caching
/**
 *
 * @type {{ add(params: { buildingIds: readonly number[]; listingIds: readonly number[] }): Promise<{ success: boolean }>; remove(params: { buildingIds: readonly number[]; listingIds: readonly number[] }): Promise<{ success: boolean }> }}
 */
export const favorites = fetcher('/api/t/1/favorites', undefined, {
  add: { method: 'POST' },
  remove: { method: 'PUT' },
})
/**
 *
 * @type {{ report: (params: ({ building_id: number; listing_id?: never } | { building_id?: never; listing_id: number }) & { category: string; email?: string; name?: string; reason?: string; origin: string }) => Promise<unknown> }}
 */
export const flag = fetcher('/api/x/1/flag', undefined, { report: { method: 'POST' } })
/**
 * @typedef {{ message_id: string, status: 200 | 201 | 202 }} SendMessageSuccessResponse
 */
/**
 * @template {number} [Status]
 * @typedef {Status extends 200 | 201 | 202 ? { message_id: string, status: Status } : { reason: string, status: Status }} _SendMessageResponse
 */
/**
 * @typedef {SendMessageSuccessResponse | _SendMessageResponse<number>} SendMessageResponse
 */
/**
 * @type {{ send: (...args: any[]) => Promise<SendMessageResponse>}}
 */
export const message = fetcher('/api/t/1/async_message/', undefined, { send: { method: 'POST' } })
/**
 * @type {{ send: (...args: any[]) => Promise<SendMessageResponse>}}
 */
export const residentQuotesChat = fetcher('/api/t/1/resident_quotes/chat', undefined, {
  send: { method: 'POST' },
})
export const user = fetcher('/api/t/1/users/:userId', User, { update: { method: 'PUT' } })

/**
 * @type {{query: (params: { token?: string }) => Promise<any>}}
 */
export const decryptToken = fetcher(
  '/api/t/1/decrypt_token',
  undefined,
  { query: { method: 'POST' } },
  {},
  null
)
/**
 * @type {{ get: (params: { email: string | undefined; uuid: string | undefined }) => Promise<import('@zcode/state-type').RawNotificationSettings>; post: (params: Pick<import('@zcode/state-type').NotificationSettings, 'email' | 'enabled' | 'push' | 'user'> & { start_date: string | Date | null }) => Promise<unknown> }}
 */
export const notificationSettings = fetcher('/api/t/1/notifications')

/**
 * @type {{ get(params: { listingId: number }): Promise<MlsListing> }}
 */
export const mlsData = fetcher('/api/t/1/listings/:listingId/mls_data', MlsListing)

export const shortTermAvailability = fetcher('/api/t/1/listings/:listingId/short_term', undefined, {
  get: { method: 'POST' },
})

/**
 * Rentals United Availability API
 *
 * @type {{get(params: { checkIn: string; checkOut: string; guests: `${number},${number}`; listingId: number | undefined; providerId: string }): Promise<RentalsUnitedAvailability>}}
 */
export const rentalsUnitedAvailability = fetcher(
  '/api/x/1/listings/short_term/:listingId/prices?check_in=:checkIn&check_out=:checkOut&provider_id=:providerId&guests=:guests',
  RentalsUnitedAvailability,
  {
    get: { method: 'GET' },
  }
)

/**
 * @type {{get(params: { listingId: number }): Promise<ShortTermPolicies>}}
 */
export const shortTermPolicies = fetcher(
  '/api/t/1/listings/short_term/:listingId/policies/',
  ShortTermPolicies,
  {
    get: { method: 'GET' },
  }
)

/**
 * @type {{get(params: { checkIn: `${number}-${number}-${number}` | string; checkOut: `${number}-${number}-${number}` | string; guests: `${number},${number}` | string; listingId: number; providerId: string }): Promise<ShortTermCancellationPolicies>}}
 */
export const shortTermCancellation = fetcher(
  '/api/x/1/listings/short_term/:listingId/policies/cancellation?check_in=:checkIn&check_out=:checkOut&provider_id=:providerId&guests=:guests',
  ShortTermCancellationPolicies,
  {
    get: { method: 'GET' },
  }
)

/**
 * @type {{query: (params: { debug?: boolean, exclude?: ReadonlyArray<'address' | 'building' | 'city' | 'hood' | 'postcode'>, lat?: number | null, lng?: number | null, query: string }) => Promise<AutocompleteResults>}}
 */
export const autocompleteResults = fetcher(
  '/api/svc/ac/2/complete',
  AutocompleteResults,
  {
    query: { method: 'GET' },
  },
  {},
  null
)

/**
 * @typedef QuerySimilarListingsRequest
 * @property {'A' | 'B' | 'C' | null} [algo]
 * @property {object | null} [filters]
 * @property {number | `${number}` | null} [group_id]
 * @property {number | null} [limit]
 * @property {number | null} [offset]
 * @property {string | null} [traktor_id]
 * @property {number | null} [user_weighting]
 */
/**
 * @type {{query: (body?: QuerySimilarListingsRequest) => Promise<ReadonlyArray<import('@zcode/state-type').Listable>>}}
 */
export const similarListings = fetcher('/api/t/1/recommend/listables', Listable, {
  query: { method: 'POST' },
})

/**
 * @type {{query: (body?: any) => Promise<ReadonlyArray<{building_id: number | null, listing_id: number }>>}}
 */
export const alcListings = fetcher('/api/t/1/listables/alc', undefined, {
  query: { method: 'POST' },
})

/**
 * @typedef QueryRelatedListingsRequest
 * @property {number} building_id
 * @property {1 | 2 | 3 | 4 | null} [limit]
 */
/**
 * @type {{query: (body?: QueryRelatedListingsRequest) => Promise<ReadonlyArray<import('@zcode/state-type').Listable>>}}
 */
export const relatedListings = fetcher('/api/t/1/buildings/:buildingId/related', Listable, {
  query: { method: 'POST' },
})

/**
 * @type {{ get: (params: { group_id: number; tour_type: import('@zcode/enums').IntegratedTourType }) => Promise<TourAvailability> }}
 */
export const tourAvailability = fetcher('/api/t/1/tour_availability/:group_id', TourAvailability)

/**
 * @type {{ get: (params: { group_id: number }) => Promise<TourIntegration> }}
 */
export const tourIntegration = fetcher('/api/t/1/tour_integration/:group_id', TourIntegration)

/**
 * @type {{
 *   submit: (body: {
 *     building_id: number | null | undefined,
 *     contact_preference: 1 | 2 | 3 | 4,
 *     detail_views: {[id: `${'b' | 'l' | 'pb' | 'pl'}`]: { viewNumber: number, viewTimes: readonly number[] }},
 *     email: string | undefined,
 *     favorites: readonly number[],
 *     filters: {[key: string]: any},
 *     floorplan_id?: number | null | undefined,
 *     listing_id?: number | null | undefined,
 *     message: string
 *     move_in: string | null
 *     name: string | undefined
 *     origin: string
 *     phone: string | null | undefined
 *     start_ons: readonly string[]
 *     tenant_tz?: string | undefined
 *     type: import('@zcode/enums').IntegratedTourType
 *     unit_id?: number | undefined,
 *   }) => Promise<{ messageId: string }>
 * }}
 */
export const tourRequests = fetcher('/api/t/2/tour_request', TourRequest, {
  submit: { method: 'POST' },
})
/**
 * @type {{get(params: { readonly token: string }): Promise<import('@zcode/bizos').CcpaRequestResponse>; save(request: import('@zcode/bizos').CcpaRequestQuery): Promise<void>}}
 */
export const ccpaRequest = fetcher('/api/t/1/ccpa/request/:token', undefined, {
  get: { method: 'GET' },
  save: { method: 'POST' },
})

export const ccpaConfirmRequest = fetcher('/api/t/1/ccpa/confirm_request', undefined, {
  update: { method: 'PUT' },
})

/**
 * LocalScores API
 *
 * @type {{query: (function({fail_on_cache_miss?: boolean; group_id?: number | `${number}`; lat: number; lng: number}): Promise<import('@zcode/state-type').RawLocationScores>)}}
 */
export const locationScores = fetcher('/api/x/1/location_scores/', undefined, {
  query: { method: 'POST', fail_on_cache_miss: true, serverRetry: 0 },
})

/**
 * Location POI API
 *
 * @type {{get: (function({ lat: number; lng: number }): Promise<import('@zcode/state-type').RawLocationPois>)}}
 */
export const locationPois = fetcher('/api/x/1/location_pois/', undefined, {
  get: { method: 'GET', serverRetry: 0 },
})

/**
 * @typedef Conversation
 * @property {string} stream_channel_id
 * @property {number | null} building_id
 * @property {number | null} listing_id
 * @property {import('@zcode/enums').ConversationStatus} status
 */
/**
 * @typedef QueryConversationsResponse
 * @property {readonly Conversation[]} conversations
 * @property {number} limit
 * @property {string|null} next_cursor
 */
/**
 * @typedef QueryConversationsParams
 * @property {readonly number[]} [building_ids]
 * @property {string} [cursor]
 * @property {number} [limit]
 * @property {readonly number[]} [listing_ids]
 */
/**
 * IAM conversations API
 *
 * @type {{query: (params?: QueryConversationsParams) => Promise<QueryConversationsResponse>}}
 */
export const streamChannels = fetcher('/api/svc/msg3/t/conversations', undefined, {
  query: { method: 'GET' },
})

/**
 * IAM unread counts API
 *
 * @type {{get: (function(): Promise<UnreadCounts>)}}
 */
export const unreadCounts = fetcher('/api/svc/msg3/u/unread', UnreadCounts, {
  get: { method: 'GET' },
})

/**
 * IAM existing channel API
 *
 * @type {{get: (function(*): Promise<any>)}}
 */
export const conversationsPoll = fetcher('/api/svc/msg3/conversations/poll', undefined, {
  get: { method: 'GET' },
})

/**
 * IAM claim conversation
 *
 * @type {{query: (function({ token: string }): Promise<StreamConversationResponse>)}}
 */
export const claimConversation = fetcher(
  '/api/svc/msg3/t/claim_conversation',
  StreamConversationResponse,
  {
    query: { method: 'POST' },
  }
)

/**
 * IAM pre-qual questions
 *
 * @type {{get: (function(*): Promise<any>)}}
 */
export const renterQuestions = fetcher('/api/t/1/profile/questions', undefined, {
  get: { method: 'GET' },
})

/**
 * IAM renter profile
 *
 * @type {{
 *   get: (function({ keys?: string }): Promise<import('@zcode/state-type').Profile>),
 *   put: (function({
 *     explicit: {[key: string]: { source: string, value: string | number }},
 *   }): Promise<import('@zcode/state-type').Profile>),
 * }}
 */
export const profile = fetcher('/api/t/1/profile/0', undefined, { get: { method: 'GET' } })
/**
 * IAM renter profile
 *
 * @type {{
 *   post: (function({
 *     explicit: {[key: string]: { source: string, value: string | number }},
 *   }): Promise<import('@zcode/state-type').Profile>),
 * }}
 */
export const profileCreate = fetcher('/api/t/1/profile', undefined, { post: { method: 'POST' } })

/**
 * IAM renter profile share
 *
 * @type {{save: (function(*): Promise<any>)}}
 */
export const profileShare = fetcher('/api/t/1/profile/0/share', undefined, {
  save: { method: 'POST' },
})

/**
 * FAQ data
 *
 * @type {{get: (function(*): Promise<import('@zcode/state-type').FaqData>)}}
 */
export const faqData = fetcher('/api/x/1/faq_data', FaqData)

/**
 * For you web recommendations API
 *
 * @type {{query: (function(*): Promise<any>)}}
 */
export const recommendedForYou = fetcher(
  '/api/t/1/for_you/unauth/recommend/v2/',
  RecommendedListablesForYou,
  { query: { method: 'POST' } }
)

/**
 * Short Term Date Unavailability API
 *
 * @type {{get: (function(*): Promise<any>)}}
 */

export const dateUnavailability = fetcher(
  '/api/t/1/listings/:listingId/short_term/unavailability/',
  undefined,
  {
    get: { method: 'GET' },
  }
)

/**
 * VRBO price and availability API
 *
 * @type {{get: (function(*): Promise<any>)}}
 */

export const vrboPriceAndAvailability = fetcher(
  '/api/svc/availability/v1/price_and_availability',
  vrboPriceAndAvailabilityBizo,
  {
    get: { method: 'GET' },
  }
)
/**
 * School widget districts
 *
 * @type {{get: (function(*): Promise<any>)}}
 */
export const widgetDistricts = fetcher(
  '/api/svc/schoolservice/v1/school_districts',
  DistrictDetails,
  { get: { method: 'GET', serverRetry: 0 } }
)

/**
 * School widget districts schools
 *
 * @type {{get: (function(*): Promise<any>)}}
 */
export const widgetDistrictSchools = fetcher(
  '/api/svc/schoolservice/v1/school_districts/:districtId/levels/:schoolLevel',
  LocationSchool,
  { get: { method: 'GET', serverRetry: 0 } }
)

/**
 * POI school tab
 *
 * @type {{get(params: { lat: number; lng: number; schoolLevel: 'elementary' | 'middle' | 'high' }): Promise<import('@zcode/state-type').Schools>}}
 */
export const poiSchools = fetcher(
  '/api/svc/schoolservice/v1/schools/levels/:schoolLevel',
  LocationSchool,
  { get: { method: 'GET', serverRetry: 0 } }
)

/**
 * POI school tab private schools
 *
 * @type {{get(params: { lat: number; lng: number }): Promise<import('@zcode/state-type').Schools>}}
 */
export const poiPrivateSchools = fetcher(
  '/api/svc/schoolservice/v1/schools/private',
  LocationSchool,
  { get: { method: 'GET', serverRetry: 0 } }
)

/**
 * POI all schools
 *
 * @type {{get: (function({ lat: number | `${number}`; limit?: number; lng: number | `${number}`; radius?: number }): Promise<import('@zcode/state-type').Schools>)}}
 */
export const poiAllSchools = fetcher('/api/svc/schoolservice/v1/schools', LocationSchool, {
  get: { method: 'GET', serverRetry: 0 },
})

/**
 * POI by category
 *
 * @type {{get: (function({ categories: Exclude<import('@zcode/state-type').CurrentSearchPOICategories, 'schools'> | ReadonlyArray<Exclude<import('@zcode/state-type').CurrentSearchPOICategories, 'schools'>>; lat: number | `${number}`; limit: number; lng: number | `${number}`; radius: number }): Promise<import('@zcode/state-type').POIs>)}}
 */
export const pois = fetcher('/api/x/1/pois', Pois, { get: { method: 'GET', serverRetry: 0 } })

/**
 * short term listables API
 *
 * @type {{query: (function(*): Promise<import('@zcode/state-type').Listables>)}}
 */
export const strListables = fetcher('/api/t/1/listables/short_term/', Listables, {
  query: { method: 'POST' },
  _pre: injectPromo,
})

/**
 * confirm email change api
 *
 * @type {{query: (function(*): Promise<any>)}}
 */
export const confirmEmailChange = fetcher(
  '/api/t/1/registration/email/confirm-change/',
  undefined,
  {
    query: { method: 'POST' },
  }
)

/**
 * Location Scores for Geographies API
 *
 * @type {{query: (function(*): Promise<import('@zcode/state-type').RawLocationScoresGeo>)}}
 */
export const locationScoresGeo = fetcher('/api/x/1/location_scores/geo', undefined, {
  query: { method: 'POST', fail_on_cache_miss: true },
})

/**
 * City guide service cities
 *
 * @type {{
 * get: (function(*): Promise<CityGuideCity>),
 * }}
 */
export const cityGuideCity = fetcher(
  '/api/svc/seocontent/external/v1/city/:cityState/:propertyType',
  CityGuideCity,
  { get: { method: 'GET', serverRetry: 0 } }
)

/**
 * City guide service neighborhoods
 *
 * @type {{
 * get: (function(*): Promise<InstanceType<CityGuideNeighborhood>>),
 * }}
 */
export const cityGuideNeighborhood = fetcher(
  '/api/svc/seocontent/external/v1/neighborhood/:cityStateHood/:propertyType',
  CityGuideNeighborhoodData,
  { get: { method: 'GET', serverRetry: 0 } }
)

/**
 * Ratings and Reviews api
 *
 * @type {{get: (function(*): Promise<any>)}}
 */
export const ratingsAndReviews = fetcher(
  '/api/t/1/reviews?listing_id=:listingId&ranges=:ranges&total=:total&sort=:sort&limit=:limit&offset=:offset',
  Reviews,
  {
    get: { method: 'GET' },
  }
)

/**
 * Detail linkpacks
 *
 * @type {{get: (payload: import('@zcode/state-type').DetailLinkPayload) => Promise<ReadonlyArray<import('@zcode/state-type').DetailLink>>}}
 */
export const detailLinkpack = fetcher(
  '/api/t/1/linkpacks/:type',
  DetailLink,
  { get: { method: 'GET' } },
  {},
  null
)

/**
 * Global nav linkpacks
 *
 * @type {{get: (payload: import('@zcode/state-type').GlobalNavLinkPayload) => Promise<GlobalNavLinks>}}
 */
export const globalNavLinkpack = fetcher(
  '/api/t/1/linkpacks/geos',
  GlobalNavLinks,
  { get: { method: 'GET' } },
  {},
  null
)

/**
 * Digital lead concierge API v2
 *
 * @type {{query: (function(*): Promise<import('@zcode/state-type').DlcMessagedProperty[]>)}}
 */
export const dlc = fetcher('/api/t/1/dlc/async_message', DlcMessagedProperties, {
  query: { method: 'POST' },
})

/**
 * @typedef BuildingRatingsParams
 * @property {readonly number} buildingId
 */
/**
 * Building rating API
 *
 * @type {{get: (payload: BuildingRatingsParams) => Promise<import('@zcode/bizos').Ratings>}}
 */
export const buildingRatings = fetcher('/api/t/1/buildings/:buildingId/rating', Ratings, {
  get: { method: 'GET' },
})

/**
 * @typedef FloorplanUnitsParams
 * @property {number} listingId
 */
/**
 * Floor plan units
 *
 * @type {{get: (function(FloorplanUnitsParams): Promise<Array<import('@zcode/bizos').Unit>>)}}
 */
export const floorplanUnits = fetcher('/api/t/1/units?listingId=:listingId', Unit, {
  get: { method: 'GET' },
})

/**
 * EngrainMap API
 *
 * @type {{
 * get: (function({ buildingId: number }): Promise<InstanceType<EngrainMap>>),
 * }}
 */
export const engrainMap = fetcher('/api/t/1/buildings/:buildingId/engrain_map', EngrainMap)

/**
 * Rent Research Details
 *
 * @type {{get: (function({ cityState: string, hoodUrl?: string }): Promise<import('@zcode/bizos').RentalData>)}}
 */
export const rentalDataDetails = fetcher(
  '/api/x/1/rent_research/details?city_state=:cityState&hood_url=:hoodUrl',
  RentalData,
  {
    get: { method: 'GET' },
  }
)

/**
 * Rent Research Homepage
 *
 * @type {{get: (function(): Promise<import('@zcode/bizos').RentalDataHomepage>)}}
 */
export const rentalDataHomepage = fetcher('/api/x/1/rent_research', RentalDataHomepage, {
  get: { method: 'GET' },
})

/**
 * Units API
 *
 * @type {{
 * get: (function({ buildingId: number }): Promise<Array<import('@zcode/bizos').Unit>>),
 * }}
 */
export const units = fetcher('/api/t/1/units?buildingId=:buildingId', Unit)

/**
 * Resident Quotes
 *
 * @type {{get: (function({ buildingId: number }): Promise<ResidentQuotes>)}}
 */
export const residentQuotes = fetcher(
  '/api/t/1/resident_quotes/buildings/:buildingId',
  ResidentQuotes,
  {
    get: { method: 'GET' },
  }
)

/**
 * Building or Address API
 *
 * @type {{query: (function({ building_id: string | number, includeWithdrawnFloorplans?: boolean, includeSupplemental?: boolean, return_address?: boolean }): Promise<import('@zcode/state-type').BuildingOrAddressType>)}}
 */
export const buildingOrAddress = fetcher('/api/t/1/pages/building_or_address', BuildingOrAddress, {
  query: { method: 'POST' },
})

/**
 * @type {{ get(params: { draft?: boolean }): Promise<RentReport> }}
 */
export const rentReport = fetcher('/api/t/1/rent_report', RentReport)

/**
 * HoodLifestyles API
 *
 * @type {{get: (function({ city: string, hood: string }): Promise<HoodLifestyles>)}}
 */
export const hoodLifestyles = fetcher('/api/t/1/lifestyles/afr/:city/:hood', HoodLifestyles)

/**
 * Address/building/listing lifestyles API
 *
 * @type {{get: (function({ content_type: 'address' | 'building' | 'listing', content_id: number }): Promise<ReadonlyArray<LifestyleScore>>)}}
 */
export const detailLifestyles = fetcher('/api/t/1/lifestyle_scores', LifestyleScore)

/**
 * Rent Research API
 *
 * @type {{
 * get: (function({ url: string}): Promise<import('@zcode/bizos').RentalDataLocation>),
 * }}
 */
export const rentResearch = fetcher('/api/x/1/rent_research/url/:url', RentalDataLocation)

/**
 * CityLifestyles API
 *
 * @type {{get: (function({ city: string }): Promise<CityLifestyles>)}}
 */
export const cityLifestyles = fetcher('/api/t/1/lifestyles/afr/:city', CityLifestyles)

/**
 * ContentModuleAmenities API
 *
 * @type {{get: (function({ city: string, hood?: string }): Promise<ContentModuleAmenities>)}}
 */
export const contentModuleAmenities = fetcher(
  '/api/t/1/amenities/afr/:city/:hood',
  ContentModuleAmenities,
  { get: { method: 'GET' } }
)

/**
 * Back of Listing Card Data API
 *
 * @type {{query: (function({ [key: string]: { pl_id?: number; pb_id?: number } }): Promise<import('@zcode/state-type').CardBackResponse>)}}
 */
export const cardBack = fetcher('/api/t/1/listables/cardback', undefined, {
  query: { method: 'POST' },
})

/**
 * Stream Token API
 *
 * @type {{get: (function({ loadType: 'active' | 'passive' | 'unread_count' | 'links' }): Promise<StreamTokenResponse>)}}
 */
export const streamToken = fetcher('/api/svc/msg3/u/stream_token/:loadType', StreamTokenResponse, {
  query: { method: 'GET' },
})

/**
 * @type {{ get(params: { url: string, draft?: boolean }): Promise<MetroRentReport> }}
 */
export const metroRentReport = fetcher('/api/t/1/rent_report/:url', MetroRentReport, {
  get: { method: 'GET' },
})
